import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Check, Minus, ShieldAlert, Pill, X } from "lucide-react";

const TYPE_CONFIG = {
  avoid: { bg: "Избягвайте", en: "Avoid", color: "#EF4444", icon: AlertTriangle },
  limit: { bg: "Ограничете", en: "Limit", color: "#F59E0B", icon: Minus },
  increase: { bg: "Увеличете", en: "Increase", color: "#10B981", icon: Check },
};

export default function MedicationAlertModal({ open, onClose, matchedDrugs = [] }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  if (!matchedDrugs || matchedDrugs.length === 0) return null;

  const avoidCount = matchedDrugs.reduce(
    (sum, d) => sum + d.interactions.filter((i) => i.type === "avoid").length,
    0
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className={`w-[95vw] max-w-[520px] max-h-[80vh] overflow-y-auto p-0 ${
          isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white"
        }`}
        data-testid="medication-alert-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-0">
          <DialogTitle
            className={`text-base font-semibold flex items-center gap-2 ${
              isDark ? "text-white" : "text-[#1A1A2E]"
            }`}
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#EF4444] to-[#F59E0B] flex items-center justify-center">
              <Pill className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <div>{bg ? "Открити лекарствени взаимодействия" : "Drug Interactions Detected"}</div>
              <div className={`text-xs font-normal mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg
                  ? `${matchedDrugs.length} медикамента, ${avoidCount} предупреждения`
                  : `${matchedDrugs.length} medications, ${avoidCount} warnings`}
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 py-4 space-y-3">
          {matchedDrugs.map((drug, di) => (
            <div
              key={`${drug.drug_en}-${di}`}
              className={`rounded-xl p-3 space-y-2 ${
                isDark ? "bg-[hsl(223,47%,10%)]" : "bg-gray-50"
              }`}
              data-testid={`alert-drug-${di}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`text-sm font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}
                >
                  {bg ? drug.drug_bg : drug.drug_en}
                </span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-700 font-medium">
                  {drug.category}
                </span>
              </div>
              <div className="space-y-1">
                {drug.interactions.map((inter, ii) => {
                  const cfg = TYPE_CONFIG[inter.type] || TYPE_CONFIG.avoid;
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={`${inter.type}-${inter.food_en}`}
                      className={`flex items-start gap-2 p-2 rounded-lg ${
                        isDark ? "bg-[hsl(223,47%,13%)]" : "bg-white"
                      }`}
                    >
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: cfg.color + "15" }}
                      >
                        <Icon className="w-3 h-3" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-[10px] font-bold uppercase"
                            style={{ color: cfg.color }}
                          >
                            {bg ? cfg.bg : cfg.en}
                          </span>
                          <span
                            className={`text-xs font-medium ${
                              isDark ? "text-gray-200" : "text-[#1A1A2E]"
                            }`}
                          >
                            {bg ? inter.food_bg : inter.food_en}
                          </span>
                        </div>
                        <p
                          className={`text-[10px] mt-0.5 ${
                            isDark ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          {bg ? inter.reason_bg : inter.reason_en}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Disclaimer */}
          <div
            className={`flex items-start gap-2 p-2.5 rounded-lg text-[10px] ${
              isDark ? "bg-amber-900/10 text-amber-400" : "bg-amber-50 text-amber-600"
            }`}
          >
            <ShieldAlert className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              {bg
                ? "Тази информация е само за справка. Винаги се консултирайте с лекар или фармацевт."
                : "This information is for reference only. Always consult a doctor or pharmacist."}
            </span>
          </div>
        </div>

        <div
          className={`flex justify-end px-5 py-3 border-t ${
            isDark ? "border-[hsl(223,47%,18%)]" : "border-gray-100"
          }`}
        >
          <Button
            onClick={onClose}
            className="gradient-primary text-white"
            data-testid="medication-alert-close-btn"
          >
            {bg ? "Разбрах" : "Got it"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
